import { navigatetoHome } from '@/utils/footer-navigations'
import React from 'react'
import { inputStyles } from '../shared/FormStyles'
import { toast } from 'sonner'

// Add this CSS animation to your global styles or use a CSS-in-JS solution
const expandAnimation = {
  enter: "transition-all duration-300 ease-out",
  enterFrom: "transform scale-95 opacity-0",
  enterTo: "transform scale-100 opacity-100",
  leave: "transition-all duration-200 ease-in",
  leaveFrom: "transform scale-100 opacity-100",
  leaveTo: "transform scale-95 opacity-0"
};

const MechanismCard = ({
  title,
  description,
  selected,
  disabled,
  onSelect,
  icon,
  requiredPlan,
}) => (
  <div
    onClick={() => !disabled && onSelect()}
    className={`
      rounded-lg border-2 transition-all relative
      ${selected ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}
      ${disabled ? 'cursor-default p-10 ' : 'hover:border-blue-200 cursor-pointer p-6 '}
    `}
  >
    <div className="flex items-start gap-4">
      <div className="text-2xl">{icon}</div>
      <div>
        <h3 className="font-semibold text-lg">{title}</h3>
        <p className="text-gray-600 text-sm mt-1">{description}</p>
      </div>
    </div>
    {disabled && (
      <div
        className="absolute inset-0 rounded-lg"
        style={{
          background:
            'linear-gradient(180deg, rgba(255,255,255,0.9) 0%, rgba(249,250,251,0.95) 100%)',
          backdropFilter: 'blur(2px)',
        }}
      >
        <div className="flex h-full flex-col items-center justify-center p-4 text-center">
          <span className="text-2xl mb-2">🔒</span>
          <h3 className="font-semibold text-lg">{title}</h3>
          <p className="text-sm font-medium text-gray-900 ">Available in {requiredPlan} Plan</p>
          <p className="text-xs text-gray-500">Upgrade to unlock this feature</p>
          <button
            className=" px-4 py-1.5 bg-blue-500 text-white text-sm font-medium rounded-full 
                       hover:bg-blue-600 transition-colors"
            onClick={(e) => {
              e.stopPropagation()
              navigatetoHome('/#prices')
            }}
          >
            Upgrade Now
          </button>
        </div>
      </div>
    )}
  </div>
)

const RewardMechanismStep = ({ plan, selected, formData, onUpdate, onNext }) => {
  // Initialize with existing mechanism if present
  const [expandedMechanism, setExpandedMechanism] = React.useState(formData?.rewardMechanism || null);

  const mechanisms = [
    {
      id: 'amount_spent',
      title: 'Spend Amount',
      description: 'Reward customers based on total amount spent',
      icon: '💰',
      available: true,
      requiredPlan: 'Basic',
    },
    {
      id: 'quantity_purchased',
      title: 'Quantity Purchased',
      description: 'Reward customers based on total items purchased',
      icon: '🛍️',
      // Fix plan validation by converting to lowercase
      available: ['premium', 'enterprise'].includes(plan?.toLowerCase()),
      requiredPlan: 'Premium',
    },
    {
      id: 'number_of_visits',
      title: 'Number of Visits',
      description: 'Reward customers based on number of visits',
      icon: '👣',
      // Fix plan validation by converting to lowercase
      available: plan?.toLowerCase() === 'enterprise',
      requiredPlan: 'Enterprise',
    },
  ]

  // Add style restrictions based on mechanism
  const styleRestrictions = {
    amount_spent: {
      allowed: ['progress'],
      message: 'Amount-based rewards can only use progress bar',
    },
    quantity_purchased: {
      allowed: ['progress', 'stamps'],
      message: 'Both stamps amd progress bar are available for quantity-based rewards',
    },
    number_of_visits: {
      allowed: ['stamps'],
      message: 'Visit-based rewards can only use stamps',
    },
  }

  const currentRestriction = styleRestrictions[formData?.rewardMechanism]

  const handleStyleSelectThroughTabs = (style) => {
    if (!currentRestriction?.allowed?.includes(style)) {
      return;
    }

    // Only update if style is different
    if (style !== formData.rewardStyle) {
      onUpdate({
        ...formData,
        rewardStyle: style,
        // Reset threshold only when style changes
        threshold: 0,
        textElements: {
          ...formData.textElements,
          progressText: {
            ...formData.textElements.progressText,
            // Clear text for stamps, percentage for progress
            text: style === 'stamps' ? '' : '__%',
          },
        },
      });
    }
  };

  const handleThresholdChange = (value) => {
    if (formData.rewardStyle === 'stamps' && value > 20) {
      toast.error('Maximum 20 stamps allowed')
      return
    }
    const threshold = parseInt(value, 10)
    onUpdate({
      ...formData,
      threshold,
      // Remove stamps initialization from here
    })
  }

  const handleMechanismSelect = (mechanismId) => {
    const mechanism = mechanisms.find(m => m.id === mechanismId);
    if (!mechanism.available) return; // Don't proceed if not available for plan

    setExpandedMechanism(mechanismId);
    const restriction = styleRestrictions[mechanismId];
    
    // Only update if mechanism changed
    if (mechanismId !== formData.rewardMechanism) {
      const newStyle = restriction.allowed.length === 1 
        ? restriction.allowed[0] 
        : (restriction.allowed.includes(formData.rewardStyle) ? formData.rewardStyle : null);

      onUpdate({
        ...formData,
        rewardMechanism: mechanismId,
        // Only update style and reset threshold if style needs to change
        ...(newStyle !== formData.rewardStyle ? {
          rewardStyle: newStyle,
          threshold: 0,
          textElements: {
            ...formData.textElements,
            progressText: {
              ...formData.textElements.progressText,
              text: newStyle === 'stamps' ? '' : '__%',
            },
          },
        } : {}),
      });
    }
  };

  // Keep mechanism expanded when returning
  React.useEffect(() => {
    if (formData?.rewardMechanism && !expandedMechanism) {
      setExpandedMechanism(formData.rewardMechanism);
    }
  }, []);

  // Add effect to auto-select style when there's only one option
  React.useEffect(() => {
    if (formData.rewardMechanism && !formData.rewardStyle) {
      const restriction = styleRestrictions[formData.rewardMechanism];
      if (restriction?.allowed.length === 1) {
        handleStyleSelectThroughTabs(restriction.allowed[0]);
      }
    }
  }, [formData.rewardMechanism]);

  const getInputMessage = () => {
    if (!formData.threshold || formData.threshold <= 0) {
      return formData.rewardStyle === 'stamps' 
        ? `Enter the number of stamps needed (max 20) to proceed !`
        : formData.rewardMechanism === 'amount_spent'
        ? 'Enter the amount customers need to spend to proceed '
        : 'Enter the number of items needs to be purchased by the customer to proceed';
    }
    return null;
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold">Choose Reward Style</h2>
        <p className="text-gray-600 mt-2">Select how customers will earn rewards</p>
      </div>

      <div className="space-y-3">
        {mechanisms.map((mechanism) => (
          <div 
            key={mechanism.id} 
            className={`
              border rounded-xl overflow-hidden transition-all duration-300
              ${selected === mechanism.id ? 'shadow-lg border-blue-500' : 'shadow-sm hover:shadow-md'}
            `}
          >
            <MechanismCard
              {...mechanism}
              selected={formData?.rewardMechanism === mechanism.id}
              disabled={!mechanism.available}
              onSelect={() => handleMechanismSelect(mechanism.id)}
            />

            {expandedMechanism === mechanism.id && (
              <div 
                className={`
                  border-t border-blue-100 bg-gradient-to-b from-blue-50/50 to-white
                  ${expandAnimation.enter}
                `}
              >
                <div className="p-6 space-y-4">
                  {/* Show restriction message first */}
                  <div className="text-center">
                    <p className="text-sm font-medium text-gray-600 bg-blue-50 py-2 px-4 rounded-full inline-block">
                      {currentRestriction?.message || 'Select your preferred style below'}
                    </p>
                  </div>

                  <div className="flex flex-col items-center gap-4">
                    <div className="flex gap-3">
                      {['stamps', 'progress'].map((style) => (
                        <button
                          key={style}
                          onClick={() => handleStyleSelectThroughTabs(style)}
                          disabled={!currentRestriction?.allowed?.includes(style)}
                          className={`
                            px-6 py-2.5 rounded-full transition-all duration-200 relative
                            font-medium text-sm min-w-[120px]
                            ${
                              formData.rewardStyle === style 
                                ? 'bg-blue-500 text-white shadow-md' 
                                : 'bg-white border-2 border-gray-200 text-gray-600 hover:border-blue-200'
                            }
                            ${
                              !currentRestriction?.allowed?.includes(style)
                                ? 'opacity-40 cursor-not-allowed line-through'
                                : 'hover:scale-105 transform'
                            }
                          `}
                        >
                          {style === 'stamps' ? '🎯 Stamps' : '📊 Progress Bar'}
                        </button>
                      ))}
                    </div>

                    {/* Threshold input with improved styling */}
                    {formData.rewardStyle && (
                      <div className="w-full max-w-xs pt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {formData.rewardStyle === 'stamps' ? 'Number of Stamps' : 'Progress Bar Target'}
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            min="1"
                            className={`
                              ${inputStyles.base}
                              text-center text-lg font-medium
                              focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                            `}
                            value={formData.threshold || ''}
                            max={formData.rewardStyle === 'stamps' ? 20 : undefined}
                            onChange={(e) => handleThresholdChange(e.target.value)}
                          />
                          <div className="mt-1 text-center">
                            <p className="text-sm text-gray-500">
                              {formData.rewardMechanism === 'amount_spent'
                                ? '💵 Amount needed to earn reward'
                                : formData.rewardMechanism === 'quantity_purchased'
                                ? '🛍️ Items needed to earn reward'
                                : '👣 Visits needed to earn reward'}
                            </p>
                          </div>
                        </div>

                        {/* Next button section - always show when input is visible */}
                        <div className="pt-6 flex flex-col items-center gap-2">
                          <button
                            onClick={onNext}
                            disabled={!formData.threshold || formData.threshold <= 0}
                            className="
                              px-8 py-3 bg-blue-500 text-white rounded-full
                              hover:bg-blue-600 hover:scale-105 transform
                              transition-all duration-200 flex items-center gap-2
                              text-sm font-medium shadow-md
                              disabled:bg-blue-300 disabled:hover:scale-100 
                              disabled:hover:bg-blue-300 disabled:cursor-not-allowed
                            "
                          >
                            Continue to Next Step
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </button>
                          
                          <p className={`text-sm  text-center ${formData.threshold > 0 ? 'text-green-500' : 'text-gray-500'}`}>
                            {getInputMessage() || '✓ Ready to proceed'}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Remove the old next button section */}
                    
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default RewardMechanismStep;
